import { memo, useMemo } from "react";

import { externalLink } from "@/routes/_constants";

import { MarkdownContent } from "@/components/features/faq-detail/ui/components";

import { Dialog, DialogContent } from "@/components/ui/dialog/dialog";
import { Image } from "@/components/ui/image/image";

import { DUMMY_IMAGE } from "@/constants/dummy-image";

import { ArticleDate } from "./article-date";
import { ProductFeedbackLink } from "./product-feedback-link";
import { convertVitepressContainers } from "../../../utils";

type Props = {
	hotelID: string;
	title: string;
	categoryLabel: string;
	thumbnailUrl: string | null;
	content: string;
	publishedAt?: string | null;
	updatedAt?: string | null;
	open: boolean;
	onOpenChange: (open: boolean) => void;
};

const resolveSupportInternalLinks = (markdown: string) => {
	return markdown.replace(/(\[[^\]]*]\()\/((?:faq|media)\/[^)\s]+)(\))/g, `$1${externalLink.faqSite}/$2$3`);
};

// CMS 由来の段落内 soft break (`\n`) を Markdown の hard break（末尾2スペース + `\n`）に変換する。
// `\n\n`（段落区切り）は維持する。hard break にすることで remark-gfm の autolink literal と干渉せず、
// 生 URL の自動リンク化と改行の両立が可能になる。
const convertSoftBreaksToHardBreaks = (markdown: string) => markdown.replace(/(?<!\n)\n(?!\n)/g, "  \n");

export const ArticleModal = memo(
	({ hotelID, title, categoryLabel, thumbnailUrl, content, publishedAt, updatedAt, open, onOpenChange }: Props) => {
		const contentWithSupportLinks = useMemo(
			() => convertSoftBreaksToHardBreaks(convertVitepressContainers(resolveSupportInternalLinks(content))),
			[content],
		);

		return (
			<Dialog open={open} onOpenChange={onOpenChange}>
				<DialogContent className="w-[calc(100%-2rem)] max-w-[800px] sm:w-full">
					<div className="space-y-6 md:space-y-8">
						<Image
							className="aspect-video w-full rounded border border-zinc-100 object-cover"
							src={thumbnailUrl ?? DUMMY_IMAGE.url}
							alt={`${title}のサムネイル`}
						/>
						<div className="py-2 md:px-8 md:py-4">
							<div className="mb-4 space-y-3">
								<span className="inline-flex rounded bg-zinc-100 px-2 py-1 text-xs font-semibold text-zinc-600">
									{categoryLabel}
								</span>
								<h4 className="break-words text-lg font-semibold md:text-xl">{title}</h4>
							</div>
							<ArticleDate publishedAt={publishedAt} updatedAt={updatedAt} />
							<div className="py-6 md:py-10">
								<MarkdownContent
									markdown={contentWithSupportLinks}
									hotelID={hotelID}
									linkVariant="inline"
								/>
								<ProductFeedbackLink hotelID={hotelID} />
							</div>
						</div>
					</div>
				</DialogContent>
			</Dialog>
		);
	},
);
